import React, { useEffect, useState } from "react";
import { Link, NavLink, Route, Routes, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Utensils, Table2, ClipboardList, BarChart3,
  Palette, CreditCard, LogOut, ExternalLink,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { t, euro } from "@/lib/i18n";
import api, { BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import Overview from "@/pages/dash/Overview";
import MenuManager from "@/pages/dash/MenuManager";
import TablesManager from "@/pages/dash/TablesManager";
import OrdersLive from "@/pages/dash/OrdersLive";
import Analytics from "@/pages/dash/Analytics";
import Branding from "@/pages/dash/Branding";
import Subscription from "@/pages/dash/Subscription";

const NAV = [
  { to: "", end: true, label: t.dash.overview, icon: LayoutDashboard, testid: "nav-overview" },
  { to: "menu", label: t.dash.menu, icon: Utensils, testid: "nav-menu" },
  { to: "tables", label: t.dash.tables, icon: Table2, testid: "nav-tables" },
  { to: "orders", label: t.dash.orders, icon: ClipboardList, testid: "nav-orders" },
  { to: "analytics", label: t.dash.analytics, icon: BarChart3, testid: "nav-analytics" },
  { to: "branding", label: t.dash.branding, icon: Palette, testid: "nav-branding" },
  { to: "subscription", label: t.dash.subscription, icon: CreditCard, testid: "nav-subscription" },
];

export default function RestaurantDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [restaurant, setRestaurant] = useState(null);

  useEffect(() => {
    api.get("/restaurants/mine").then((r) => setRestaurant(r.data)).catch(() => {});
  }, []);

  const doLogout = async () => {
    await logout();
    nav("/");
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[260px_1fr] bg-background">
      <aside className="border-r border-border bg-card p-6 flex flex-col gap-8">
        <Link to="/" className="font-display text-2xl font-black" data-testid="dash-brand-link">
          Tavolo<span className="text-[hsl(var(--accent))]">OS</span>
        </Link>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">
          {restaurant?.name || user?.name}
        </div>
        <nav className="flex flex-col gap-1">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-secondary text-foreground/80"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                {n.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="mt-auto flex flex-col gap-2">
          <Button variant="ghost" className="justify-start" onClick={doLogout} data-testid="dash-logout-btn">
            <LogOut className="h-4 w-4 mr-2" /> {t.nav.logout}
          </Button>
        </div>
      </aside>

      <main className="min-w-0">
        <div className="max-w-6xl mx-auto p-8">
          <Routes>
            <Route index element={<Overview restaurant={restaurant} />} />
            <Route path="menu" element={<MenuManager />} />
            <Route path="tables" element={<TablesManager restaurant={restaurant} />} />
            <Route path="orders" element={<OrdersLive restaurant={restaurant} />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="branding" element={<Branding restaurant={restaurant} onUpdate={setRestaurant} />} />
            <Route path="subscription" element={<Subscription restaurant={restaurant} />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

export { euro, BACKEND_URL };
